import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonComponent,
  InputComponent,
  TableComponent,
  type ActionEvent,
  type TableConfig,
} from 'nebula-ui-kit';
import { PageHeaderAction, PageHeaderComponent } from '@nebula-shared/ui';
import { finalize } from 'rxjs';
import { readVaultItemsPerPageFromPreferences, VAULT_TABLE_PAGE_SIZE_OPTIONS } from '@nebula/shared-vault/data-access';
import {
  bindVaultTablePageSizeToPreferences,
  createVaultTablePageSizeSignal,
} from '../../vault-table-pagination-bind.util';
import { PreservationTraceService } from '../../../../../data-access/src/lib/ingest/preservation-trace.service';
import {
  IngestSummary,
  PreservationMetrics,
  QuarantinedIngest,
} from '../../../../../domain/src/lib/ingest/preservation-trace.model';

/** Fila de cuarentena con etiquetas ya formateadas para `lib-table`. */
interface QuarantinedRow extends QuarantinedIngest {
  motivosLabel: string;
  fechaLabel: string;
}

/** Fila del listado de todas las ingestas con etiquetas formateadas. */
interface IngestRow extends IngestSummary {
  estadoLabel: string;
  fechaLabel: string;
  fixityLabel: string;
}

/**
 * Tablero de trazabilidad de preservación: métricas agregadas (semáforo del
 * tenant), expedientes en cuarentena con su motivo, y acceso al recorrido del
 * archivo de cualquier ingesta (por la tabla o por ID). Consume
 * /api/preservation-trace (metrics + quarantined).
 */
@Component({
  selector: 'app-preservation-trace-dashboard',
  standalone: true,
  imports: [ButtonComponent, InputComponent, TableComponent, PageHeaderComponent],
  templateUrl: './preservation-trace-dashboard.component.html',
  styleUrls: ['./preservation-trace-dashboard.component.scss'],
})
export class PreservationTraceDashboardComponent implements OnInit {
  private readonly traceService = inject(PreservationTraceService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly metrics = signal<PreservationMetrics | null>(null);
  readonly ingests = signal<IngestRow[]>([]);
  readonly ingestsTotal = signal(0);
  readonly quarantined = signal<QuarantinedRow[]>([]);

  /** ID de ingesta para abrir su recorrido directamente. */
  ingestIdToOpen = '';

  readonly tablePageSize = createVaultTablePageSizeSignal();
  readonly tablePageSizeOptions = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];

  readonly headerActions = computed<PageHeaderAction[]>(() => [
    {
      id: 'reload',
      label: 'Actualizar',
      icon: 'RefreshCcw',
      variant: 'secondary',
      loading: this.loading(),
    },
  ]);

  readonly tableConfig = signal<TableConfig<QuarantinedRow>>({
    columns: [
      { key: 'ingest_id', label: 'Ingesta', sortable: true, align: 'left', width: '110px' },
      { key: 'upload_id', label: 'Upload ID', sortable: false, align: 'left' },
      { key: 'fechaLabel', label: 'Fecha', sortable: true, align: 'left', width: '190px' },
      { key: 'motivosLabel', label: 'Motivos', sortable: false, align: 'left' },
    ],
    actions: [{ label: 'Ver recorrido', icon: 'view', action: 'recorrido' }],
    showPagination: true,
    rowClickable: true,
    defaultAction: 'recorrido',
    pageSize: readVaultItemsPerPageFromPreferences(),
    pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
  });

  readonly ingestsTableConfig = signal<TableConfig<IngestRow>>({
    columns: [
      { key: 'ingest_id', label: 'Ingesta', sortable: true, align: 'left', width: '100px' },
      { key: 'company_name', label: 'Empresa', sortable: true, align: 'left' },
      { key: 'estadoLabel', label: 'Estado', sortable: true, align: 'center', width: '140px' },
      { key: 'fixityLabel', label: 'Fixity', sortable: false, align: 'center', width: '110px' },
      { key: 'fechaLabel', label: 'Fecha', sortable: true, align: 'left', width: '190px' },
    ],
    actions: [{ label: 'Ver recorrido', icon: 'view', action: 'recorrido' }],
    showPagination: true,
    rowClickable: true,
    defaultAction: 'recorrido',
    pageSize: readVaultItemsPerPageFromPreferences(),
    pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
  });

  /** Conteo por estado para las insignias del semáforo. */
  readonly estados = computed(() => {
    const m = this.metrics();
    if (!m) return [];
    return Object.entries(m.por_estado).map(([estado, n]) => ({ estado, n }));
  });

  readonly tasaCuarentenaPct = computed(() => {
    const m = this.metrics();
    return m ? Math.round(m.tasa_cuarentena * 1000) / 10 : 0;
  });

  constructor() {
    bindVaultTablePageSizeToPreferences(this.tablePageSize, this.destroyRef);
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);

    this.traceService
      .getMetrics()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (m) => this.metrics.set(m),
        error: (err) =>
          this.error.set(
            err?.error?.detail || err?.message || 'No se pudieron cargar las métricas.',
          ),
      });

    this.traceService
      .getIngests()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (resp) => {
          this.ingestsTotal.set(resp.total);
          this.ingests.set(
            resp.items.map((i) => ({
              ...i,
              estadoLabel: i.estado || '—',
              fechaLabel: i.created_at ? new Date(i.created_at).toLocaleString() : '—',
              fixityLabel:
                i.fixity_ok === true ? 'OK' : i.fixity_ok === false ? 'FALLÓ' : '—',
            })),
          );
        },
        error: (err) =>
          this.error.set(
            err?.error?.detail || err?.message || 'No se pudieron cargar las ingestas.',
          ),
      });

    this.traceService
      .getQuarantined()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list) =>
          this.quarantined.set(
            list.map((q) => ({
              ...q,
              motivosLabel: q.motivos.length ? q.motivos.join(', ') : '—',
              fechaLabel: q.created_at ? new Date(q.created_at).toLocaleString() : '—',
            })),
          ),
        error: (err) =>
          this.error.set(
            err?.error?.detail || err?.message || 'No se pudo cargar la cuarentena.',
          ),
      });
  }

  onHeaderAction(actionId: string): void {
    if (actionId === 'reload') this.load();
  }

  onTableAction(ev: ActionEvent<QuarantinedRow>): void {
    if (ev.action === 'recorrido') this.openRecorrido(ev.data.ingest_id);
  }

  onIngestAction(ev: ActionEvent<IngestRow>): void {
    if (ev.action === 'recorrido') this.openRecorrido(ev.data.ingest_id);
  }

  openRecorrido(ingestId: number): void {
    void this.router.navigate(['recorrido', ingestId], { relativeTo: this.route.parent });
  }

  openById(): void {
    const id = Number(this.ingestIdToOpen.trim());
    if (Number.isFinite(id) && id > 0) {
      this.openRecorrido(id);
    } else {
      this.error.set('Ingresa un ID de ingesta válido.');
    }
  }
}
